import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { C, withAlpha } from "../../constants/theme";
import { toast } from "../../hooks/useToast";
import { Btn } from "../ui/Btn";
import { MP_AMBIENTE, gerarPixPedido, verificarPagamentoPedido, novoRequestId, mensagemMP } from "../../utils/mercadoPago";
import { useVerificacaoAutomatica } from "../../hooks/useVerificacaoAutomatica";

/* Pix pela conta Mercado Pago do vendedor, dentro de "Receber pagamento".
   Este componente NUNCA marca o pedido como pago: quem faz isso é o backend,
   depois de confirmar com o Mercado Pago (webhook ou "Verificar pagamento").
   A tela só acompanha o espelho pedido.mp_cobranca, que o servidor grava. */

const AZUL_MP = "#009ee3";

// Resumo da conexão (sem nada sensível) — para saber se dá para cobrar
export function useStatusMercadoPago(uid) {
  const [status, setStatus] = useState(undefined);
  useEffect(() => {
    if (!uid || !MP_AMBIENTE) { setStatus(null); return undefined; }
    return onSnapshot(doc(db, "users", uid, "mercadopago", MP_AMBIENTE), s => setStatus(s.exists() ? s.data() : null), () => setStatus(null));
  }, [uid]);
  return status;
}

export function PixMercadoPago({ pedido, conectado, onPago }) {
  const [email, setEmail] = useState(pedido?.cliente_email || "");
  const [simular, setSimular] = useState(false);
  const [cobranca, setCobranca] = useState(null);
  const [qr, setQr] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const requestId = useRef(novoRequestId());
  const avisado = useRef(false);

  const espelho = pedido?.mp_cobranca;
  // Pago pelo espelho do pedido (servidor) ou pela resposta de uma consulta desta tela
  const [confirmadoAqui, setConfirmadoAqui] = useState(false);
  const pago = (!!cobranca && espelho?.id === cobranca.cobrancaId && espelho?.situacao === "pago") || confirmadoAqui;

  useEffect(() => {
    let atual = true;
    setQr("");
    if (cobranca?.pix?.codigo) QRCode.toDataURL(cobranca.pix.codigo, { width: 240, margin: 1 }).then(url => { if (atual) setQr(url); }).catch(() => {});
    return () => { atual = false; };
  }, [cobranca]);

  // Tela aberta: confere sozinha, sem depender do aviso (webhook) do Mercado Pago
  const situacao = espelho?.id === cobranca?.cobrancaId ? espelho?.situacao : cobranca?.situacao;
  useVerificacaoAutomatica(!!cobranca?.cobrancaId && !pago && !["falhou", "estornado", "revisao"].includes(situacao),
    async () => { const r = await verificarPagamentoPedido(cobranca.cobrancaId); if (r?.situacao === "pago") setConfirmadoAqui(true); });

  useEffect(() => {
    if (pago && !avisado.current) { avisado.current = true; toast("✅ Pagamento confirmado pelo Mercado Pago.", "success"); onPago?.(); }
  }, [pago, onPago]);

  if (!conectado) {
    return (
      <div style={{ fontSize:12, color:C.warning, lineHeight:1.6, background:withAlpha(C.warning, "12"), border:"1px solid "+withAlpha(C.warning, "44"), borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
        Conecte sua conta Mercado Pago em <strong>Configurações → Pagamentos</strong> para gerar Pix com confirmação automática.
      </div>
    );
  }

  const gerar = async () => {
    setOcupado(true);
    try { setCobranca(await gerarPixPedido({ pedidoId: pedido.id, requestId: requestId.current, email: email.trim(), simularAprovacao: simular })); }
    catch (e) { toast(mensagemMP(e), "error"); }
    finally { setOcupado(false); }
  };
  const verificar = async () => {
    setOcupado(true);
    try {
      const r = await verificarPagamentoPedido(cobranca.cobrancaId);
      if (r.situacao === "pago") setConfirmadoAqui(true);
      else toast(r.situacao === "aguardando" ? "Ainda não consta pagamento no Mercado Pago." : "O Mercado Pago não confirmou este pagamento. Confira na sua conta.", "info");
    } catch (e) { toast(mensagemMP(e), "error"); }
    finally { setOcupado(false); }
  };
  const copiar = async () => {
    try { await navigator.clipboard.writeText(cobranca.pix.codigo); toast("📋 Pix copiado!", "success"); }
    catch { toast("Não foi possível copiar", "error"); }
  };

  return (
    <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:10 }}>
      {!cobranca && <>
        <label htmlFor="mp-pix-email" style={{ color:C.muted, fontSize:10, fontWeight:700, letterSpacing:1.5 }}>E-MAIL DO CLIENTE (EXIGIDO PELO MERCADO PAGO)</label>
        <input id="mp-pix-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="cliente@email.com"
          style={{ width:"100%", boxSizing:"border-box", background:C.card, border:"1px solid "+C.border, borderRadius:10, color:C.text, padding:"9px 12px", fontSize:13, outline:"none" }} />
        {MP_AMBIENTE === "teste" && (
          <label style={{ display:"flex", gap:8, alignItems:"flex-start", fontSize:12, color:C.muted, cursor:"pointer", lineHeight:1.5 }}>
            <input type="checkbox" checked={simular} onChange={e => setSimular(e.target.checked)} style={{ marginTop:2 }} />
            <span><strong style={{ color:C.warning }}>Só no teste:</strong> simular pagamento aprovado (o Mercado Pago aprova sozinho alguns instantes depois — serve para testar a confirmação automática).</span>
          </label>
        )}
        <Btn color={AZUL_MP} onClick={gerar} disabled={ocupado}>{ocupado ? "Gerando…" : "⚡ Gerar Pix Mercado Pago"}</Btn>
      </>}

      {cobranca && !pago && <>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>Pix de <strong style={{ color:C.text }}>R$ {Number(cobranca.valor).toFixed(2)}</strong> na sua conta Mercado Pago:</div>
          {qr ? <img src={qr} alt="QR Code Pix do Mercado Pago" style={{ width:220, height:220, borderRadius:12, background:"#fff", padding:8, boxSizing:"border-box" }} />
            : <div style={{ fontSize:12, color:C.muted, padding:20 }}>⏳ Gerando QR Code…</div>}
        </div>
        <Btn outline color={AZUL_MP} onClick={copiar}>📋 Copiar código Pix</Btn>
        <div role="status" style={{ fontSize:12, color:C.muted, textAlign:"center" }}>⏳ Aguardando pagamento — a confirmação chega sozinha.</div>
        <Btn small outline onClick={verificar} disabled={ocupado}>{ocupado ? "Consultando…" : "Verificar pagamento agora"}</Btn>
      </>}

      {pago && (
        <div role="status" style={{ fontSize:13, fontWeight:800, color:C.success, textAlign:"center", background:withAlpha(C.success, "14"), border:"1px solid "+withAlpha(C.success, "55"), borderRadius:10, padding:"12px 14px" }}>
          ✅ Pagamento confirmado pelo Mercado Pago e registrado no pedido.
        </div>
      )}

      <div style={{ fontSize:11, color:C.muted, lineHeight:1.5 }}>
        🔒 O pedido só é marcado como pago depois que o servidor confirma o pagamento diretamente com o Mercado Pago.
      </div>
    </div>
  );
}
